/**
 * integrations moduli uchun umumiy turlar/konstantalar (API + veb + mobil).
 * Faqat integrations moduli egasi tahrirlaydi.
 *
 * Bu yerda — enumlar (status, ish turi, maqsad) va SOF mantiq (qoldiq qoidalari, kutish oynasi),
 * UI ham, server ham bir xil hisoblashi uchun.
 */
import type { L10n } from '../i18n';
import type { Tone } from '../statuses';

export interface Labeled extends L10n {
  tone: Tone;
}

const def = <K extends string>(m: Record<K, Labeled>) => m;

// ------------------------------------------------------------------ Ulanishlar
/** Ulanish turi: savdo kanali (marketplace/sayt), zaxira nusxa (MoySklad), CRM, xabar (Telegram). */
export const CONNECTION_KINDS = def({
  sales_channel: { uz: 'Savdo kanali', ru: 'Канал продаж', en: 'Sales channel', zh: '销售渠道', tone: 'violet' },
  crm: { uz: 'CRM', ru: 'CRM', en: 'CRM', zh: 'CRM', tone: 'info' },
  mirror: { uz: 'Zaxira nusxa', ru: 'Резервная копия', en: 'Mirror copy', zh: '备份镜像', tone: 'neutral' },
  messaging: { uz: 'Xabar almashish', ru: 'Мессенджер', en: 'Messaging', zh: '消息通道', tone: 'accent' },
});
export type ConnectionKind = keyof typeof CONNECTION_KINDS;
export const CONNECTION_KIND_KEYS = Object.keys(CONNECTION_KINDS) as ConnectionKind[];

export const CONNECTION_STATUSES = def({
  connected: { uz: 'Ulangan', ru: 'Подключено', en: 'Connected', zh: '已连接', tone: 'success' },
  error: { uz: 'Xato', ru: 'Ошибка', en: 'Error', zh: '错误', tone: 'danger' },
  disabled: { uz: 'O‘chirilgan', ru: 'Отключено', en: 'Disabled', zh: '已关闭', tone: 'neutral' },
  pending: { uz: 'Sozlanmoqda', ru: 'Настройка', en: 'Configuring', zh: '配置中', tone: 'warning' },
});
export type ConnectionStatus = keyof typeof CONNECTION_STATUSES;
export const CONNECTION_STATUS_KEYS = Object.keys(CONNECTION_STATUSES) as ConnectionStatus[];

// ------------------------------------------------------------------ Sinxronlash
export const SYNC_JOBS = def({
  orders_import: { uz: 'Buyurtmalar importi', ru: 'Импорт заказов', en: 'Order import', zh: '订单导入', tone: 'violet' },
  order_confirm: { uz: 'Buyurtma tasdiqlash', ru: 'Подтверждение заказов', en: 'Order confirmation', zh: '订单确认', tone: 'accent' },
  stock_push: { uz: 'Qoldiq yuborish', ru: 'Выгрузка остатков', en: 'Stock push', zh: '库存同步', tone: 'info' },
  products_import: { uz: 'Tovarlar importi', ru: 'Импорт товаров', en: 'Product import', zh: '商品导入', tone: 'neutral' },
  labels: { uz: 'Yorliqlar', ru: 'Этикетки', en: 'Labels', zh: '标签', tone: 'neutral' },
  mirror_products: { uz: 'MoySklad: tovarlar', ru: 'МойСклад: товары', en: 'MoySklad: products', zh: 'MoySklad：商品', tone: 'neutral' },
  mirror_stock: { uz: 'MoySklad: qoldiq', ru: 'МойСклад: остатки', en: 'MoySklad: stock', zh: 'MoySklad：库存', tone: 'neutral' },
  mirror_orders: { uz: 'MoySklad: buyurtmalar', ru: 'МойСклад: заказы', en: 'MoySklad: orders', zh: 'MoySklad：订单', tone: 'neutral' },
  mirror_shipments: { uz: 'MoySklad: otgruzkalar', ru: 'МойСклад: отгрузки', en: 'MoySklad: shipments', zh: 'MoySklad：出库单', tone: 'success' },
  cancel_watch: { uz: 'Bekor kuzatuvi', ru: 'Наблюдение за отменами', en: 'Cancellation watch', zh: '取消监控', tone: 'warning' },
  invoices_import: { uz: 'Nakladnoylar importi', ru: 'Импорт накладных', en: 'Invoice import', zh: '运单导入', tone: 'info' },
  marking_retry: { uz: 'Markirovka qayta urinishi', ru: 'Повтор маркировки', en: 'Marking retry', zh: '标识重试', tone: 'warning' },
});
export type SyncJob = keyof typeof SYNC_JOBS;
export const SYNC_JOB_KEYS = Object.keys(SYNC_JOBS) as SyncJob[];

/** Qo'lda ishga tushirish mumkin bo'lgan ishlar (POST /connections/:id/sync). */
export const MANUAL_SYNC_JOBS = [
  'orders_import',
  'order_confirm',
  'stock_push',
  'products_import',
  'mirror_products',
  'mirror_stock',
  'mirror_orders',
  'mirror_shipments',
  'cancel_watch',
  'invoices_import',
] as const;
export type ManualSyncJob = (typeof MANUAL_SYNC_JOBS)[number];

export const SYNC_RUN_STATUSES = def({
  running: { uz: 'Bajarilmoqda', ru: 'Выполняется', en: 'Running', zh: '执行中', tone: 'info' },
  ok: { uz: 'Muvaffaqiyatli', ru: 'Успешно', en: 'Succeeded', zh: '成功', tone: 'success' },
  partial: { uz: 'Qisman', ru: 'Частично', en: 'Partial', zh: '部分完成', tone: 'warning' },
  error: { uz: 'Xato', ru: 'Ошибка', en: 'Error', zh: '错误', tone: 'danger' },
});
export type SyncRunStatus = keyof typeof SYNC_RUN_STATUSES;

export const SYNC_TRIGGERS = def({
  job: { uz: 'Jadval', ru: 'По расписанию', en: 'Schedule', zh: '定时', tone: 'neutral' },
  manual: { uz: 'Qo‘lda', ru: 'Вручную', en: 'Manual', zh: '手动', tone: 'accent' },
  webhook: { uz: 'Webhook', ru: 'Webhook', en: 'Webhook', zh: 'Webhook', tone: 'info' },
  event: { uz: 'Hodisa', ru: 'Событие', en: 'Event', zh: '事件', tone: 'violet' },
});
export type SyncTrigger = keyof typeof SYNC_TRIGGERS;

// ------------------------------------------------------------------ Webhooklar
export const WEBHOOK_STATUSES = def({
  pending: { uz: 'Navbatda', ru: 'В очереди', en: 'Queued', zh: '排队中', tone: 'warning' },
  processed: { uz: 'Qayta ishlandi', ru: 'Обработан', en: 'Processed', zh: '已处理', tone: 'success' },
  ignored: { uz: 'E’tiborsiz', ru: 'Пропущен', en: 'Ignored', zh: '已忽略', tone: 'neutral' },
  error: { uz: 'Xato', ru: 'Ошибка', en: 'Error', zh: '错误', tone: 'danger' },
});
export type WebhookStatus = keyof typeof WEBHOOK_STATUSES;

// ------------------------------------------------------------------ Listinglar (SKU ↔ marketplace)
export const LISTING_STATUSES = def({
  linked: { uz: 'Bog‘langan', ru: 'Связан', en: 'Linked', zh: '已匹配', tone: 'success' },
  unlinked: { uz: 'Bog‘lanmagan', ru: 'Не связан', en: 'Not linked', zh: '未匹配', tone: 'warning' },
  blocked: { uz: 'Bloklangan', ru: 'Заблокирован', en: 'Blocked', zh: '已封禁', tone: 'danger' },
});
export type ListingStatus = keyof typeof LISTING_STATUSES;
export const LISTING_STATUS_KEYS = Object.keys(LISTING_STATUSES) as ListingStatus[];

// ------------------------------------------------------------------ Import qilingan buyurtmalar
export const IMPORTED_ORDER_STATUSES = def({
  processing: { uz: 'Ishlanmoqda', ru: 'Обработка', en: 'Processing', zh: '处理中', tone: 'info' },
  held: { uz: 'Kutish oynasida', ru: 'В окне ожидания', en: 'In the hold window', zh: '等待窗口中', tone: 'warning' },
  imported: { uz: 'Import qilindi', ru: 'Импортирован', en: 'Imported', zh: '已导入', tone: 'info' },
  confirm_pending: { uz: 'Tasdiq navbatda', ru: 'Ожидает подтверждения', en: 'Awaiting confirmation', zh: '待确认', tone: 'warning' },
  confirmed: { uz: 'Tasdiqlandi', ru: 'Подтверждён', en: 'Confirmed', zh: '已确认', tone: 'success' },
  canceled: { uz: 'Bekor qilindi', ru: 'Отменён', en: 'Cancelled', zh: '已取消', tone: 'danger' },
  skipped: { uz: 'O‘tkazib yuborildi', ru: 'Пропущен', en: 'Skipped', zh: '已跳过', tone: 'neutral' },
  failed: { uz: 'Xato', ru: 'Ошибка', en: 'Error', zh: '错误', tone: 'danger' },
});
export type ImportedOrderStatus = keyof typeof IMPORTED_ORDER_STATUSES;

/**
 * TASDIQLASH TO'SIG'I — buyurtma KANALDA tasdiqlanmaydi (Stocker qoidasi, audit §3.1).
 * To'siq WMS dan keladi (`wms.order.no_stock`, markirovka) va o'zi yechiladi.
 */
export const CONFIRM_BLOCKS = def({
  no_stock: { uz: 'Tovar yo‘q', ru: 'Нет товара', en: 'Out of stock', zh: '缺货', tone: 'danger' },
  marking: { uz: 'Markirovka kerak', ru: 'Нужна маркировка', en: 'Marking required', zh: '需要标识码', tone: 'warning' },
});
export type ConfirmBlock = keyof typeof CONFIRM_BLOCKS;

// ------------------------------------------------------------------ Bekor kuzatuvi (Stocker `uzum/cancelSync.js`)
export const CANCEL_WATCH_STATUSES = def({
  watching: { uz: 'Kuzatuvda', ru: 'Под наблюдением', en: 'Watching', zh: '监控中', tone: 'info' },
  reported: { uz: 'Ariza berildi', ru: 'Заявка подана', en: 'Request submitted', zh: '已提交申请', tone: 'warning' },
  closed: { uz: 'Yopildi', ru: 'Закрыто', en: 'Closed', zh: '已关闭', tone: 'neutral' },
});
export type CancelWatchStatus = keyof typeof CANCEL_WATCH_STATUSES;

/**
 * KUZATUV SO'ROVLARI ORALIG'I (Stocker: yangi buyurtma tez-tez, eskisi siyrak so'raladi —
 * kabinet bo'yicha kunlik limitni yemasin). Xato bo'lsa — eksponensial orqaga chekinish.
 *
 * @param ageMs   buyurtma tasdiqlangandan beri o'tgan vaqt
 * @param errors  ketma-ket xatolar soni (0 — oxirgi so'rov muvaffaqiyatli)
 */
export function cancelWatchDelayMs(ageMs: number, errors = 0): number {
  if (errors > 0) return Math.min(10 * 60_000 * 2 ** (errors - 1), 3 * 3_600_000);
  const h = ageMs / 3_600_000;
  if (h < 6) return 10 * 60_000;
  if (h < 24) return 30 * 60_000;
  return 60 * 60_000;
}

/** Keyingi so'rov vaqti — kuzatuv oynasidan (`watchUntil`) chiqib ketmaydi. */
export function nextCancelCheckAt(
  p: { confirmedAt?: Date | string | number | null; watchUntil?: Date | string | number | null; errors?: number },
  now: Date | number = Date.now(),
): Date {
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const started = p.confirmedAt ? new Date(p.confirmedAt).getTime() : nowMs;
  const next = nowMs + cancelWatchDelayMs(Math.max(0, nowMs - started), p.errors ?? 0);
  const until = p.watchUntil ? new Date(p.watchUntil).getTime() : null;
  return new Date(until && next > until ? until : next);
}

// ------------------------------------------------------------------ MoySklad zaxira navbati
export const MIRROR_QUEUE_STATUSES = def({
  pending: { uz: 'Navbatda', ru: 'В очереди', en: 'Queued', zh: '排队中', tone: 'warning' },
  sent: { uz: 'Yuborildi', ru: 'Отправлено', en: 'Sent', zh: '已发送', tone: 'success' },
  failed: { uz: 'Xato', ru: 'Ошибка', en: 'Error', zh: '错误', tone: 'danger' },
});
export type MirrorQueueStatus = keyof typeof MIRROR_QUEUE_STATUSES;

export const MIRROR_ENTITIES = def({
  product: { uz: 'Tovar', ru: 'Товар', en: 'Product', zh: '商品', tone: 'info' },
  stock: { uz: 'Qoldiq', ru: 'Остаток', en: 'Stock', zh: '库存', tone: 'accent' },
  order: { uz: 'Buyurtma', ru: 'Заказ', en: 'Order', zh: '订单', tone: 'violet' },
  shipment: { uz: 'Otgruzka', ru: 'Отгрузка', en: 'Shipment', zh: '出库单', tone: 'success' },
});
export type MirrorEntity = keyof typeof MIRROR_ENTITIES;

export const MIRROR_OPS = ['upsert', 'delete', 'state', 'create'] as const;
export type MirrorOp = (typeof MIRROR_OPS)[number];

/** Navbat yozuvi necha marta urinib, keyin `failed` da qoladi (qo'lda "Qayta yuborish"). */
export const MIRROR_MAX_ATTEMPTS = 5;

/**
 * OTGRUZKA KECHIKISHI (Stocker `moysklad/demand.js`: standart 60 s).
 *
 * Buyurtma "Yig'ildi" bo'lgach otgruzka DARHOL yaratilmaydi: zaxira tizimida o'sha buyurtma hali
 * paydo bo'lmagan bo'lishi mumkin (holat navbati hali yetib bormagan), bundan tashqari MoySklad
 * ssenariysi (scripttemplate) hali yoqiq bo'lsa — o'zi yaratib ulguradi va biz dublikat ochmaymiz.
 */
export const MIRROR_SHIPMENT_DELAY_MS = 60_000;

// ------------------------------------------------------------------ Telegram
export const BOT_PURPOSES = def({
  support: { uz: 'Qo‘llab-quvvatlash', ru: 'Поддержка', en: 'Support', zh: '客服支持', tone: 'accent' },
  notifications: { uz: 'Bildirishnomalar', ru: 'Уведомления', en: 'Notifications', zh: '通知', tone: 'info' },
  money_requests: { uz: 'Pul so‘rovlari', ru: 'Денежные запросы', en: 'Money requests', zh: '资金申请', tone: 'warning' },
  reports: { uz: 'Hisobotlar', ru: 'Отчёты', en: 'Reports', zh: '报表', tone: 'violet' },
});
export type BotPurpose = keyof typeof BOT_PURPOSES;
export const BOT_PURPOSE_KEYS = Object.keys(BOT_PURPOSES) as BotPurpose[];

export const CHAT_PURPOSES = def({
  alerts: { uz: 'Ogohlantirishlar', ru: 'Оповещения', en: 'Alerts', zh: '预警', tone: 'danger' },
  reports: { uz: 'Hisobotlar', ru: 'Отчёты', en: 'Reports', zh: '报表', tone: 'violet' },
  couriers: { uz: 'Kuryerlar', ru: 'Курьеры', en: 'Couriers', zh: '配送员', tone: 'accent' },
  cancel_requests: { uz: 'Bekor qilish so‘rovlari', ru: 'Запросы на отмену', en: 'Cancellation requests', zh: '取消申请', tone: 'warning' },
  money_requests: { uz: 'Pul so‘rovlari', ru: 'Денежные запросы', en: 'Money requests', zh: '资金申请', tone: 'warning' },
  support: { uz: 'Qo‘llab-quvvatlash', ru: 'Поддержка', en: 'Support', zh: '客服支持', tone: 'info' },
  hr: { uz: 'Xodimlar', ru: 'Кадры', en: 'Employees', zh: '员工', tone: 'neutral' },
  general: { uz: 'Umumiy', ru: 'Общий', en: 'General', zh: '通用', tone: 'neutral' },
});
export type ChatPurpose = keyof typeof CHAT_PURPOSES;
export const CHAT_PURPOSE_KEYS = Object.keys(CHAT_PURPOSES) as ChatPurpose[];

export const TELEGRAM_CHAT_TYPES = ['private', 'group', 'supergroup', 'channel'] as const;
export type TelegramChatType = (typeof TELEGRAM_CHAT_TYPES)[number];

// ------------------------------------------------------------------ Provayderlar (adapter metama'lumoti)
export type ConfigFieldType = 'text' | 'secret' | 'number' | 'select' | 'time-range' | 'boolean' | 'table';

/** Jadval maydonining ustuni (`type: 'table'`). */
export interface ConfigColumn {
  key: string;
  label: string;
  /** `readonly` — tashqi tizimdan kelgan, tahrirlanmaydi (do'kon ID si, nomi). */
  type: 'text' | 'select' | 'readonly';
  /** `select` uchun ro'yxat manbasi — `GET .../options/<kalit>`. */
  optionsFrom?: string;
  hint?: string;
}

/** UI formasi shu ta'rifdan avtomatik quriladi (`GET /integrations/providers`). */
export interface ConfigField {
  key: string;
  label: string;
  type: ConfigFieldType;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  /**
   * Ro'yxat TASHQI TIZIMDAN keladi (`GET /integrations/connections/:id/options/<kalit>`).
   * Qo'lda ID ko'chirishning o'rniga: token kiritilgach ro'yxat o'zi to'ladi.
   */
  optionsFrom?: string;
  /** `table` uchun ustunlar; qatorlar `config[key]` da massiv bo'lib saqlanadi. */
  columns?: ConfigColumn[];
  /** `table` uchun qator kaliti — shu ustun bo'yicha qatorlar solishtiriladi. */
  rowKey?: string;
  default?: string | number | boolean;
}

/** Tashqi ro'yxat elementi (yuridik shaxs, ombor, sotuv kanali). */
export interface ConfigOption {
  value: string;
  label: string;
}

export interface ProviderCapabilities {
  orders: boolean;
  confirm: boolean;
  cancel: boolean;
  labels: boolean;
  stock: boolean;
  products: boolean;
  webhooks: boolean;
  /** Kanal nakladnoylari (FBS/FBO/qaytarish) — wms ga `saveChannelInvoice()` orqali tushadi. */
  invoices: boolean;
}

export const CAPABILITY_LABELS: Record<keyof ProviderCapabilities, string> = {
  orders: 'Buyurtmalar',
  confirm: 'Tasdiqlash',
  cancel: 'Bekor qilish',
  labels: 'Yorliqlar',
  stock: 'Qoldiq',
  products: 'Tovarlar',
  webhooks: 'Webhook',
  invoices: 'Nakladnoylar',
};

/** Kim ulay oladi: hamkor o'z do'konini, platforma — CRM/zaxira/Telegram. */
export type ProviderScope = 'seller' | 'platform' | 'both';

export interface ProviderInfo {
  code: string;
  title: string;
  kind: ConnectionKind;
  /** marketplace | crm | site | mirror | messaging */
  category: string;
  description: string;
  /** Logotip o'rniga monogramma (UI). */
  monogram: string;
  /** Brend rangi (hex) — kartochka uni YUMSHATIB ishlatadi, to'g'ridan-to'g'ri emas. */
  brand?: string | null;
  /** Logotip fayli (`/providers/<kod>.png`). Bo'lmasa monogramma chiziladi. */
  logo?: string | null;
  scope: ProviderScope;
  comingSoon: boolean;
  capabilities: ProviderCapabilities;
  configSchema: ConfigField[];
  /** Ulanish boshqa bo'limda boshqariladi (masalan, Telegram — "Telegram" sahifasi). */
  managedIn?: 'telegram' | null;
}

// ------------------------------------------------------------------ Qoldiq qoidalari (Stocker `uzum_stock_mod`)
export const STOCK_RULE_COMPARISONS = def({
  lte: { uz: '≤ (kichik yoki teng)', ru: '≤', en: '≤ (less or equal)', zh: '≤（小于等于）', tone: 'neutral' },
  gte: { uz: '≥ (katta yoki teng)', ru: '≥', en: '≥ (greater or equal)', zh: '≥（大于等于）', tone: 'neutral' },
  eq: { uz: '= (teng)', ru: '=', en: '= (equal)', zh: '=（等于）', tone: 'neutral' },
});
export type StockRuleComparison = keyof typeof STOCK_RULE_COMPARISONS;
export const STOCK_RULE_COMPARISON_KEYS = Object.keys(STOCK_RULE_COMPARISONS) as StockRuleComparison[];

export interface StockRuleLike {
  quantityFrom: number;
  comparison: StockRuleComparison | string;
  priority: number;
  quantityTo: number;
  /** null — hamkorning barcha ulanishlari uchun. */
  connectionId?: string | null;
  isActive?: boolean;
}

function ruleMatches(qty: number, r: StockRuleLike): boolean {
  if (r.comparison === 'lte') return qty <= r.quantityFrom;
  if (r.comparison === 'gte') return qty >= r.quantityFrom;
  if (r.comparison === 'eq') return qty === r.quantityFrom;
  return false;
}

/**
 * Qoidalarni qo'llash: ulanishga xos qoidalar umumiylaridan oldin, so'ng `priority` o'sish tartibida;
 * BIRINCHI mos kelgani g'olib (Stocker mantiqi). Mos kelmasa — miqdor o'zgarmaydi.
 */
export function applyStockRules(qty: number, rules: readonly StockRuleLike[]): number {
  const sorted = rules
    .filter((r) => r.isActive !== false)
    .slice()
    .sort((a, b) => (a.connectionId ? 0 : 1) - (b.connectionId ? 0 : 1) || a.priority - b.priority);
  const hit = sorted.find((r) => ruleMatches(qty, r));
  return hit ? Math.max(0, Math.floor(hit.quantityTo)) : qty;
}

/**
 * Marketplace'ga yuboriladigan son:
 *   1) ombordagi mavjud (available) dona — manfiy/kasr → 0/pastga yaxlitlash;
 *   2) `cardQuantity` ga bo'lish (kartochkada 2 dona bo'lsa, 7 dona → 3 kartochka);
 *   3) qoldiq qoidalari (masalan, "≤ 2 bo'lsa 0 yubor").
 * `available` noma'lum (null/undefined/NaN) bo'lsa — `null`: HECH NARSA yuborilmaydi (himoya).
 */
export function computePushQty(
  available: number | null | undefined,
  cardQuantity: number,
  rules: readonly StockRuleLike[] = [],
): number | null {
  if (available === null || available === undefined || !Number.isFinite(available)) return null;
  const units = Math.max(0, Math.floor(available));
  const per = Math.max(1, Math.floor(Number(cardQuantity) || 1));
  const cards = Math.floor(units / per);
  return applyStockRules(cards, rules);
}

export interface StockPushInput {
  listingId: string;
  skuId: string;
  externalSkuId: string;
  cardQuantity: number;
  lastStock: number | null;
  missingCount: number;
}

export interface StockPushPlan {
  /** Yuboriladigan qatorlar. */
  push: { listingId: string; externalSkuId: string; qty: number }[];
  /** Qoldiq topilmagan, hali 0 yuborilmaydigan listinglar (missingCount oshiriladi). */
  missing: string[];
  /** Butun yuborish bekor qilindi (himoya ishladi) — sababi. */
  aborted: string | null;
}

/** Necha marta ketma-ket topilmasa "tugagan" (0) deb hisoblanadi (Stocker: 3×). */
export const STOCK_MISSING_LIMIT = 3;
/** Qoldiq hisobotida so'ralgan SKU larning kamida shuncha ulushi bo'lishi shart (Stocker: 95%). */
export const STOCK_MIN_COVERAGE = 0.95;

/**
 * Qoldiq yuborish rejasi — himoyalar bilan:
 *   - qoldiq xaritasi umuman yo'q (wms javob bermadi) → bekor;
 *   - xaritada so'ralgan SKU larning < 95% i bor → bekor (chala hisobot);
 *   - alohida SKU topilmasa — 3 marta ketma-ket topilmaguncha yuborilmaydi, keyin 0;
 *   - avval qoldig'i bo'lgan listinglarning yarmidan ko'pi birdan 0 ga tushsa (≥ 10 ta) → bekor (shubhali).
 */
export function planStockPush(
  listings: readonly StockPushInput[],
  availability: ReadonlyMap<string, number> | null,
  rules: readonly StockRuleLike[] = [],
): StockPushPlan {
  if (!availability)
    return { push: [], missing: [], aborted: 'Qoldiq ma’lumoti olinmadi — hech narsa yuborilmadi' };
  const skuIds = new Set(listings.map((l) => l.skuId));
  if (skuIds.size > 0) {
    let covered = 0;
    for (const id of skuIds) if (availability.has(id)) covered++;
    if (covered / skuIds.size < STOCK_MIN_COVERAGE) {
      return {
        push: [],
        missing: [],
        aborted: `Qoldiq hisoboti chala (${covered}/${skuIds.size}) — yuborish bekor qilindi`,
      };
    }
  }
  const push: StockPushPlan['push'] = [];
  const missing: string[] = [];
  let hadStock = 0;
  let droppedToZero = 0;
  for (const l of listings) {
    const avail = availability.get(l.skuId);
    let qty: number | null;
    if (avail === undefined) {
      if (l.missingCount + 1 < STOCK_MISSING_LIMIT) {
        missing.push(l.listingId);
        continue;
      }
      qty = 0;
    } else {
      qty = computePushQty(avail, l.cardQuantity, rules);
    }
    if (qty === null) continue;
    if ((l.lastStock ?? 0) > 0) {
      hadStock++;
      if (qty === 0) droppedToZero++;
    }
    push.push({ listingId: l.listingId, externalSkuId: l.externalSkuId, qty });
  }
  if (hadStock >= 10 && droppedToZero / hadStock > 0.5) {
    return {
      push: [],
      missing,
      aborted: `Shubhali: ${droppedToZero}/${hadStock} ta listing birdan 0 ga tushyapti — yuborish to‘xtatildi`,
    };
  }
  return { push, missing, aborted: null };
}

// ------------------------------------------------------------------ Kutish oynasi (hold window)
/** "HH:mm" → kun boshidan daqiqa; noto'g'ri bo'lsa null. */
export function parseHHMM(v: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** "22:00-06:00" → {startMin, endMin}; bo'sh yoki noto'g'ri bo'lsa null (oyna yo'q). */
export function parseHoldWindow(v: string | null | undefined): { startMin: number; endMin: number } | null {
  if (!v) return null;
  const parts = String(v)
    .split(/[-–—]/)
    .map((s) => s.trim());
  if (parts.length !== 2) return null;
  const startMin = parseHHMM(parts[0]);
  const endMin = parseHHMM(parts[1]);
  if (startMin === null || endMin === null || startMin === endMin) return null;
  return { startMin, endMin };
}

/** Toshkent vaqti bo'yicha kun boshidan daqiqa (UTC+5, doimiy). */
export function tashkentMinutes(now: Date | number = Date.now()): number {
  const ms = typeof now === 'number' ? now : now.getTime();
  const t = new Date(ms + 5 * 3600_000);
  return t.getUTCHours() * 60 + t.getUTCMinutes();
}

/**
 * Hozir kutish oynasidami. Yarim ochiq oraliq [start, end); oyna yarim tunni kesib o'tishi mumkin
 * (22:00–06:00). Oynada kelgan marketplace buyurtmalari darhol tasdiqlanmaydi — mijoz bekor qilishi
 * mumkin bo'lgan tungi buyurtmalar ertalab ishlanadi (Stocker tajribasi).
 */
export function inHoldWindow(window: string | null | undefined, now: Date | number = Date.now()): boolean {
  const w = parseHoldWindow(window);
  if (!w) return false;
  const min = tashkentMinutes(now);
  return w.startMin <= w.endMin ? min >= w.startMin && min < w.endMin : min >= w.startMin || min < w.endMin;
}

/** Kutish oynasi tugaydigan vaqt (oynada bo'lmasa — null). */
export function holdWindowEnd(
  window: string | null | undefined,
  now: Date | number = Date.now(),
): Date | null {
  const w = parseHoldWindow(window);
  if (!w || !inHoldWindow(window, now)) return null;
  const ms = typeof now === 'number' ? now : now.getTime();
  const cur = tashkentMinutes(ms);
  let diff = w.endMin - cur;
  if (diff <= 0) diff += 24 * 60;
  const end = new Date(ms + diff * 60_000);
  end.setUTCSeconds(0, 0);
  return end;
}

/** Tone ni label map'dan olish (UI badge uchun). */
export function labelOf(map: Record<string, Labeled>, code: string | null | undefined): Labeled {
  return (code && map[code]) || { uz: code ?? '—', ru: code ?? '—', tone: 'neutral' };
}
